import React, { useEffect, useState } from "react";
import { FaCheckDouble, FaExclamationTriangle, FaTimesCircle } from "react-icons/fa";
import AdminModal from "../AdminModal";
import { colors, spacing, typography, radius, btnPrimary, btnSecondary, transitions } from "../../utils/designTokens";

// Confirmación de "Aprobar y publicar": qué va a pasar, qué falta (bloqueos),
// qué conviene revisar (avisos) y la confirmación de que la mesa aprobó el
// acta. El flujo (PDF, subida, publicar) lo corre el Editor y acá se muestra
// el paso en curso.
//
// Props:
//   open, onClose
//   chequeo       { bloqueos: string[], avisos: string[] }
//   numeroPrevisto "004"
//   edificioNombre
//   progreso      { paso: string, pct?: number } | null
//   publicando    boolean
//   onConfirmar   () => void

export default function ActaPublicarModal({ open, onClose, chequeo, numeroPrevisto, edificioNombre, progreso, publicando, onConfirmar }) {
  const [confirmado, setConfirmado] = useState(false);
  useEffect(() => {
    if (open) setConfirmado(false);
  }, [open]);

  const bloqueos = (chequeo && chequeo.bloqueos) || [];
  const avisos = (chequeo && chequeo.avisos) || [];
  const puede = !publicando && confirmado && bloqueos.length === 0;

  return (
    <AdminModal
      open={open}
      onClose={publicando ? () => {} : onClose}
      title="Aprobar y publicar el acta"
      size="medium"
      icon={<FaCheckDouble />}
      footer={
        <div style={S.footer}>
          <button type="button" style={btnSecondary} className="habitia-btn-secondary" onClick={onClose} disabled={publicando}>
            Volver
          </button>
          <button
            type="button"
            style={{ ...btnPrimary, opacity: puede ? 1 : 0.6 }}
            className="habitia-btn-primary"
            onClick={onConfirmar}
            disabled={!puede}
          >
            <FaCheckDouble /> {publicando ? "Publicando…" : "Aprobar y publicar"}
          </button>
        </div>
      }
    >
      <p style={S.texto}>
        Vas a publicar el acta{numeroPrevisto ? ` N° ${numeroPrevisto}` : ""}
        {edificioNombre ? ` de ${edificioNombre}` : ""}. Al publicarla:
      </p>
      <ul style={S.lista}>
        <li>
          Se genera el PDF tal como se ve en la vista previa y queda guardado. El número lo confirma el servidor: si otra
          acta del edificio se publica antes, el PDF se genera de nuevo con el que corresponda.
        </li>
        <li>
          Se avisa a cada propietario por WhatsApp (con el PDF) o por email (con un enlace para verla en Habitia). Los
          inquilinos no la reciben.
        </li>
        <li>El acta queda bloqueada: después no se puede editar.</li>
      </ul>

      {bloqueos.length > 0 && (
        <div style={{ ...S.caja, background: colors.errorLight }}>
          <span style={{ ...S.cajaTitulo, color: colors.errorDark }}>
            <FaTimesCircle aria-hidden="true" /> Antes de publicar
          </span>
          <ul style={S.listaCaja}>
            {bloqueos.map((b) => (
              <li key={b}>{b}</li>
            ))}
          </ul>
        </div>
      )}

      {avisos.length > 0 && (
        <div style={{ ...S.caja, background: colors.warningLight }}>
          <span style={{ ...S.cajaTitulo, color: colors.warningDark }}>
            <FaExclamationTriangle aria-hidden="true" /> Revisá
          </span>
          <ul style={S.listaCaja}>
            {avisos.map((a) => (
              <li key={a}>{a}</li>
            ))}
          </ul>
        </div>
      )}

      <label style={S.check} htmlFor="acta-publicar-confirmo">
        <input
          id="acta-publicar-confirmo"
          type="checkbox"
          checked={confirmado}
          onChange={(e) => setConfirmado(e.target.checked)}
          disabled={publicando || bloqueos.length > 0}
          style={{ width: 16, height: 16, accentColor: colors.primary, cursor: "pointer", marginTop: 2 }}
        />
        <span>Confirmo que el acta fue revisada y aprobada por la mesa de la asamblea (presidencia y secretaría).</span>
      </label>

      {progreso && (
        <div style={S.progreso} aria-live="polite">
          <span style={S.hint}>{progreso.paso}</span>
          {progreso.pct != null && (
            <div style={S.barra} role="progressbar" aria-label="Progreso de la publicación" aria-valuemin={0} aria-valuemax={100} aria-valuenow={Math.round(progreso.pct)}>
              <div style={{ ...S.barraRelleno, width: `${Math.max(0, Math.min(100, progreso.pct))}%` }} />
            </div>
          )}
        </div>
      )}
    </AdminModal>
  );
}

const S = {
  footer: { display: "flex", gap: spacing[2], justifyContent: "flex-end", width: "100%", flexWrap: "wrap" },
  texto: { marginTop: 0, fontSize: typography.sizes.md, color: colors.gray900, lineHeight: 1.5 },
  lista: { margin: `0 0 ${spacing[4]}px`, paddingLeft: spacing[5], fontSize: typography.sizes.md, color: colors.gray900, lineHeight: 1.6 },
  caja: { borderRadius: radius.sm, padding: `${spacing[3]}px ${spacing[4]}px`, marginBottom: spacing[3] },
  cajaTitulo: { display: "flex", alignItems: "center", gap: spacing[2], fontSize: typography.sizes.md, marginBottom: spacing[1] },
  listaCaja: { margin: 0, paddingLeft: spacing[5], fontSize: typography.sizes.base, color: colors.gray900, lineHeight: 1.6 },
  check: {
    display: "flex",
    alignItems: "flex-start",
    gap: spacing[2],
    fontSize: typography.sizes.md,
    color: colors.gray900,
    cursor: "pointer",
    lineHeight: 1.5,
  },
  progreso: { display: "flex", flexDirection: "column", gap: spacing[1], marginTop: spacing[4] },
  hint: { fontSize: typography.sizes.sm, color: colors.gray600 },
  barra: { height: 8, background: colors.gray100, borderRadius: radius.full, overflow: "hidden" },
  barraRelleno: { height: "100%", background: colors.primary, transition: `width ${transitions.normal}` },
};
